// frontend/src/components/WorkspaceSplitter.tsx
import React, { useCallback, useRef } from 'react';

/**
 * A draggable, KEYBOARD-OPERABLE divider between two workspace surfaces,
 * implementing the ARIA window-splitter pattern (role="separator" with
 * aria-valuenow). It controls the WIDTH of one adjacent panel; the other side
 * is a flex-1 canvas that absorbs whatever the panel gives up.
 *
 * WHY THIS EXISTS AS A COMPONENT AND NOT A STYLED DIV WITH onMouseDown: a
 * mouse-only resizer in this product would be a WCAG 2.1.1 failure we then
 * sell an accessibility tool through. Every splitter in the editor is this
 * one, so none of them can ship without the keyboard half.
 *
 * THE VALUE IS COMPONENT STATE IN THE PARENT, NEVER localStorage. This repo
 * has paid twice for persisted layout state going stale (see the fullscreen
 * notes in PDFViewer for the most recent accounting); a splitter position is
 * a gesture about the current window, not a preference about every future one.
 *
 * KEYBOARD MODEL, per the ARIA practices: the arrow keys move the SEPARATOR,
 * not "the panel". ArrowRight moves the separator right; what that does to the
 * panel's width depends on which side the panel is on, which is what
 * `panelSide` encodes. Home and End take the panel to its min and max. The
 * step is 16px — big enough to feel, small enough to aim with.
 *
 * Pointer events rather than mouse events, with setPointerCapture: one code
 * path covers mouse, pen and touch, and capture means a fast drag that leaves
 * the 6px strip does not drop the resize mid-gesture.
 */

/** Width change per arrow-key press, in CSS px. */
export const SPLITTER_KEY_STEP_PX = 16;

interface WorkspaceSplitterProps {
  /** Accessible name — say which panel this resizes, e.g. "Resize the field list". */
  label: string;
  /** The controlled panel's current width in CSS px — also aria-valuenow. */
  value: number;
  /** Narrowest the panel may go. The clamp lives HERE so no caller can crush a panel. */
  min: number;
  /** Widest the panel may go, so no panel can crush the canvas either. */
  max: number;
  onChange: (next: number) => void;
  /**
   * Which side of this separator the controlled panel sits on. 'left': the
   * separator moving right grows the panel (the field list). 'right': the
   * separator moving right shrinks it (the properties rail).
   */
  panelSide: 'left' | 'right';
  /** id of the panel this separator resizes, for aria-controls. */
  controlsId?: string;
}

const WorkspaceSplitter: React.FC<WorkspaceSplitterProps> = ({
  label,
  value,
  min,
  max,
  onChange,
  panelSide,
  controlsId,
}) => {
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const clamp = useCallback(
    (next: number) => Math.min(max, Math.max(min, next)),
    [min, max]
  );

  // Pointer-x delta → panel width. A panel on the LEFT of the separator grows
  // as the pointer moves right; one on the RIGHT grows as it moves left.
  const widthFromPointer = useCallback(
    (clientX: number) => {
      const start = dragStart.current;
      if (!start) return value;
      const delta = clientX - start.x;
      return clamp(panelSide === 'left' ? start.width + delta : start.width - delta);
    },
    [clamp, panelSide, value]
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Primary button / first touch only — a right-click is a context menu,
    // not a resize.
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    dragStart.current = { x: event.clientX, width: value };
    // Optional-called: jsdom implements pointer events but not pointer
    // capture, and a resize that works without capture (just less robustly
    // off the strip) beats one that throws in every test environment.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    onChange(widthFromPointer(event.clientX));
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    dragStart.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // The ARROWS MOVE THE SEPARATOR, so left/right mean opposite width changes
    // on opposite sides. Home/End are the panel's own extremes either side.
    let next: number | null = null;
    if (event.key === 'ArrowRight') {
      next = clamp(panelSide === 'left' ? value + SPLITTER_KEY_STEP_PX : value - SPLITTER_KEY_STEP_PX);
    } else if (event.key === 'ArrowLeft') {
      next = clamp(panelSide === 'left' ? value - SPLITTER_KEY_STEP_PX : value + SPLITTER_KEY_STEP_PX);
    } else if (event.key === 'Home') {
      next = min;
    } else if (event.key === 'End') {
      next = max;
    }
    if (next !== null) {
      // preventDefault so arrows resize the panel instead of also scrolling
      // whatever scroller happens to contain the separator.
      event.preventDefault();
      if (next !== value) onChange(next);
    }
  };

  return (
    // A focusable div, which the file-wide "no tabindex, ever" rule in
    // PDFViewer explicitly anticipates exceptions to being argued: this one IS
    // an interactive control — a separator with a value — not a scroll region
    // promoted to a tab stop. It slips past isTypingTarget, which is correct:
    // Space on a separator has no separator meaning, so momentary pan is the
    // right thing for it to do, exactly as it does on the canvas.
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-controls={controlsId}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      title={`${label} — drag, or press the arrow keys`}
      className="w-1.5 flex-shrink-0 self-stretch rounded-full cursor-col-resize bg-gray-200 hover:bg-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
    />
  );
};

export default WorkspaceSplitter;
